import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Pause, RotateCcw, Volume2, VolumeX, Crown, Check, Loader2, Youtube } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PublishDialog } from "@/components/publish/PublishDialog";
import { YT_ON_HOLD, YtBuildBadge } from "@/components/publish/YtBuild";

export const TONE = { hype: "#ff5a36", funny: "#ffcc4d", cinematic: "#3ad7ff" };

export const ScoreRing = ({ score, color, size = 12, testid = "variant-score" }) => (
  <div className={`relative shrink-0`} style={{ width: size * 4, height: size * 4 }} data-testid={testid}>
    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
      <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
      <circle cx="18" cy="18" r="15.5" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeDasharray={`${(score / 100) * 97.4} 97.4`} />
    </svg>
    <span className="absolute inset-0 grid place-items-center font-display font-bold text-white" style={{ fontSize: size * 1.15 }}>{score}</span>
  </div>
);

const Column = ({ c, best, vref, heard, onHear, onKeep, keeping, onPublish }) => {
  const { t } = useI18n();
  const color = TONE[c.variant] || "#a746ff";
  return (
    <div className="snap-center shrink-0 w-[78vw] sm:w-auto sm:flex-1 max-w-[340px] flex flex-col gap-2" data-testid={`compare-col-${c.variant}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{t(`var.${c.variant}`)}</span>
            {best && <span className="flex items-center gap-1 text-[10px] font-semibold text-black rounded-full px-2 py-0.5" style={{ background: color }} data-testid="compare-best-badge"><Crown className="w-3 h-3" />{t("var.best")}</span>}
          </div>
          <div className="text-[11px] text-slate-300 line-clamp-1">{c.title}</div>
        </div>
        <ScoreRing score={c.viral?.score ?? 0} color={color} size={13} testid={`compare-score-${c.variant}`} />
      </div>
      <button type="button" onClick={onHear} data-testid={`compare-video-${c.variant}`} className="relative aspect-[9/16] w-full max-h-[52vh] sm:max-h-[62vh] mx-auto rounded-2xl overflow-hidden bg-black border-2 transition-[border-color,box-shadow] duration-300"
        style={{ borderColor: heard ? color : "rgba(255,255,255,0.08)", boxShadow: heard ? `0 0 28px ${color}55` : "none" }}>
        {c.status === "done" ? <video ref={vref} src={mediaUrl(`/clips/${c.id}/file`)} muted={!heard} loop playsInline preload="auto" className="w-full h-full object-cover" />
          : <span className="absolute inset-0 grid place-items-center text-xs text-slate-400"><Loader2 className="w-5 h-5 animate-spin" style={{ color }} /></span>}
        <span className="absolute top-2 left-2 rounded-full bg-black/60 backdrop-blur px-2 py-1 text-[10px] text-white flex items-center gap-1">
          {heard ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}{t(heard ? "cmp.listening" : "cmp.tap_listen")}
        </span>
      </button>
      <ul className="space-y-0.5 min-h-[3.2rem]">{(c.viral?.tips || []).slice(0, 3).map((tip, i) => <li key={i} className="text-[10px] text-slate-400 leading-snug">• {tip}</li>)}</ul>
      <button onClick={onKeep} disabled={c.status !== "done" || keeping} data-testid={`compare-keep-${c.variant}`}
        className="rounded-full py-2.5 text-sm font-semibold text-black flex items-center justify-center gap-2 transition-[transform,opacity] hover:scale-[1.02] disabled:opacity-40" style={{ background: color }}>
        {keeping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}{t("cmp.keep")}
      </button>
      <button onClick={onPublish} disabled={YT_ON_HOLD || c.status !== "done"} data-testid={`compare-publish-${c.variant}`} title={YT_ON_HOLD ? t("pub.build_hint") : undefined}
        className="rounded-full py-2 text-xs font-semibold text-white border border-[#ff0033]/50 hover:bg-[#ff0033]/15 flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
        <Youtube className="w-3.5 h-3.5 text-[#ff0033]" />{t("pub.btn")}
      </button>
      {YT_ON_HOLD && <div className="flex justify-center"><YtBuildBadge testid={`compare-yt-build-${c.variant}`} /></div>}
    </div>
  );
};

export const VariantsCompare = ({ open, onOpenChange, gid, clips, bestId, onKept }) => {
  const { t } = useI18n();
  const refs = useRef({});
  const [playing, setPlaying] = useState(false);
  const [heard, setHeard] = useState(null);
  const [keeping, setKeeping] = useState(null);
  const [pub, setPub] = useState(null);
  const vids = () => clips.map((c) => refs.current[c.id]).filter(Boolean);
  const playAll = () => {
    const v = vids(); const t0 = v[0]?.currentTime || 0;
    Promise.allSettled(v.map((x) => { x.currentTime = t0; return x.play(); })).then((r) => setPlaying(r.some((p) => p.status === "fulfilled")));
  };
  const pauseAll = () => { vids().forEach((x) => x.pause()); setPlaying(false); };
  const restart = () => { vids().forEach((x) => { x.currentTime = 0; }); playAll(); };
  useEffect(() => {
    if (!open) { setPlaying(false); return undefined; }
    setHeard(bestId);
    const id = setTimeout(playAll, 400);
    const sync = setInterval(() => {
      const v = vids();
      if (v.length < 2) return;
      v.slice(1).forEach((x) => { if (Math.abs(x.currentTime - v[0].currentTime) > 0.25 && v[0].currentTime < x.duration) x.currentTime = v[0].currentTime; });
    }, 1000);
    return () => { clearTimeout(id); clearInterval(sync); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const keep = async (c) => {
    setKeeping(c.id);
    try {
      const { data } = await api.post(`/clips/variants/${gid}/keep`, { clip_id: c.id });
      toast.success(t("cmp.kept", { n: data.removed }));
      onKept(c.id);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setKeeping(null); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1100px] w-[96vw] max-h-[96vh] overflow-y-auto bg-[#07070c]/95 backdrop-blur-xl border-white/10 p-4 sm:p-6" data-testid="variants-compare-dialog">
        <DialogTitle className="font-display text-lg text-white">{t("cmp.title")}</DialogTitle>
        <p className="text-xs text-slate-400 -mt-2">{t("cmp.desc")}</p>
        <div className="flex items-center gap-2">
          <button onClick={playing ? pauseAll : playAll} data-testid="compare-play-btn" className="cx-btn rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1.5">
            {playing ? <><Pause className="w-3.5 h-3.5" />{t("cmp.pause")}</> : <><Play className="w-3.5 h-3.5" />{t("cmp.play")}</>}
          </button>
          <button onClick={restart} data-testid="compare-restart-btn" className="cx-ghost rounded-full px-4 py-2 text-xs flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" />{t("cmp.restart")}</button>
        </div>
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 sm:justify-center" data-testid="compare-grid">
          {clips.map((c) => (
            <Column key={c.id} c={c} best={c.id === bestId} heard={heard === c.id} keeping={keeping === c.id}
              vref={(el) => { refs.current[c.id] = el; }} onHear={() => setHeard(c.id)} onKeep={() => keep(c)} onPublish={() => { pauseAll(); setPub(c); }} />
          ))}
        </div>
        <PublishDialog clip={pub} open={!!pub} onOpenChange={(o) => !o && setPub(null)} />
      </DialogContent>
    </Dialog>
  );
};
